import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import SortIcon from '@mui/icons-material/Sort'
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Popover,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import AIRoadmapResponse from './AIRoadmapResponse'
import CatalogGrid from './CatalogGrid'
import { useRoadmap } from './RoadmapContext'
import { SERVICE_LINE_COLORS, SERVICE_LINE_ICONS } from './icons'
import {
  CatalogItem,
  classifyItem,
  getPhaseShortLabel,
  normalizeServiceLine,
  Phase,
  SERVICE_LINE_LABELS,
} from './types'
import { getVariantLabel, groupItemsByVariant, ItemGroup } from './variantGrouping'

type SortMode = 'relevance' | 'points_asc' | 'points_desc' | 'alpha'

// ─── Quick Action Chips for AI Strategy Assistant ──────────────────────────
const AI_QUICK_ACTIONS = [
  {
    label: 'Build my roadmap',
    prompt:
      'Build a complete roadmap for this client based on their profile, objectives, and budget. Fill all phases with recommended services.',
  },
  {
    label: 'Focus months 1-3 on SEO',
    prompt:
      'Focus months 1 through 3 heavily on SEO services. Prioritize SEO audits, keyword research, and technical SEO items in the early phases.',
  },
  {
    label: 'Remove all paid media items',
    prompt: 'Remove all paid media / PPC items from the roadmap. This client does not need paid advertising services.',
  },
  {
    label: 'More strategic items early on',
    prompt:
      'Shift more strategic items (audits, assessments, research, planning) into the setup phase and months 1-2. Move executable items to later months.',
  },
  {
    label: 'Prioritize quick wins',
    prompt:
      'Prioritize quick-win items that can show results early. Focus on low-effort, high-impact services in the first few months.',
  },
  {
    label: 'Explain the current strategy',
    prompt:
      'Explain the current roadmap strategy. What is the reasoning behind the current phase allocation and service selection? What could be improved?',
  },
]

// ─── Variant Group Card ────────────────────────────────────────────────────

const VariantGroupCard: React.FC<{
  group: ItemGroup
  activePhaseItemIds: Set<string>
  getItemPhases: (productId: string) => Set<Phase>
  removeFromAll: (productId: string) => void
  addItem: (item: CatalogItem) => void
  handleOpenMultiMonth: (event: React.MouseEvent<HTMLElement>, item: CatalogItem) => void
  effectiveRate: number
  slColor: string
  SlIcon: any
  groupLabel: string
  isSetupPhase: boolean
  activePhase: Phase
  recommendedSkus: Map<string, string>
}> = ({
  group,
  activePhaseItemIds,
  getItemPhases,
  removeFromAll,
  addItem,
  handleOpenMultiMonth,
  effectiveRate,
  slColor,
  SlIcon,
  groupLabel,
  isSetupPhase,
  activePhase,
  recommendedSkus,
}) => {
  const theme = useTheme()
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'

  // Default to middle variant
  const defaultIndex = Math.floor((group.items.length - 1) / 2)
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex)
  const selectedItem = group.items[selectedIndex]

  // Check if any variant from this group is added in active phase
  const addedVariant = group.items.find(item => activePhaseItemIds.has(item.products_uuid))
  const isAnyAdded = !!addedVariant
  const isSelectedAdded = activePhaseItemIds.has(selectedItem.products_uuid)

  // Check if ANY variant is AI-recommended
  const anyRecommended = group.items.some(i => i.sku && recommendedSkus.has(i.sku))
  const selectedAiReason = selectedItem.sku ? recommendedSkus.get(selectedItem.sku) : undefined

  // All phases any variant appears in
  const allVariantPhases = useMemo(() => {
    const phases = new Set<Phase>()
    group.items.forEach(item => {
      getItemPhases(item.products_uuid).forEach(p => phases.add(p))
    })
    return phases
  }, [group.items, getItemPhases])

  const dollarValue = (selectedItem.points || 0) * effectiveRate
  const cat = classifyItem(selectedItem)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isAnyAdded
          ? alpha(theme.palette.text.complete, 0.4)
          : anyRecommended
          ? alpha(aiAccent, 0.4)
          : 'divider',
        bgcolor: isAnyAdded
          ? alpha(theme.palette.text.complete, 0.04)
          : anyRecommended
          ? alpha(aiAccent, 0.04)
          : 'background.paper',
        transition: 'all 0.15s',
        overflow: 'hidden',
        '&:hover': {
          borderColor: isAnyAdded
            ? alpha(theme.palette.text.complete, 0.5)
            : anyRecommended
            ? alpha(aiAccent, 0.6)
            : alpha(theme.palette.text.primary, 0.15),
          transform: 'translateY(-1px)',
          boxShadow: anyRecommended ? `0 2px 12px ${alpha(aiAccent, 0.2)}` : '0 2px 8px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Card header */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        {/* Category + service line + AI badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Chip
            label={cat === 'strategic' ? 'Strategic' : 'Executable'}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 600,
              bgcolor:
                cat === 'strategic' ? alpha(theme.palette.text.link, 0.12) : alpha(theme.palette.text.highlight, 0.12),
              color: cat === 'strategic' ? theme.palette.text.link : theme.palette.text.highlight,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {SlIcon && (
            <Chip
              icon={<SlIcon sx={{ fontSize: 12, color: slColor }} />}
              label={groupLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                bgcolor: `${slColor}18`,
                color: slColor,
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.25 },
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {anyRecommended && (
              <Tooltip title={selectedAiReason || 'AI Recommended'} arrow>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: aiAccent }} />
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Title + variant count */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            mb: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {group.baseName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
          {group.items.length} variants
        </Typography>
      </Box>

      {/* Variant selector pills */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {group.items.map((variant, idx) => {
          const label = getVariantLabel(variant.title, group.baseName)
          const isSelected = idx === selectedIndex
          const isThisAdded = activePhaseItemIds.has(variant.products_uuid)

          return (
            <Chip
              key={variant.products_uuid}
              label={`${label} · ${variant.points}pts`}
              size="small"
              onClick={() => setSelectedIndex(idx)}
              sx={{
                height: 24,
                fontSize: 11,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                border: '1px solid',
                ...(isThisAdded
                  ? {
                      bgcolor: alpha(theme.palette.text.complete, 0.1),
                      borderColor: alpha(theme.palette.text.complete, 0.5),
                      color: theme.palette.text.complete,
                    }
                  : isSelected
                  ? {
                      bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.1),
                      borderColor: alpha(theme.palette.text.ai_accent_secondary, 0.4),
                      color: theme.palette.text.ai_accent_secondary,
                    }
                  : {
                      bgcolor: 'transparent',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      '&:hover': { borderColor: 'text.primary', color: 'text.primary' },
                    }),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )
        })}
      </Box>

      {/* Points + price for selected variant */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: slColor }}>
          {selectedItem.points || 0} pts
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ${dollarValue.toLocaleString()}
        </Typography>
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <Tooltip
            title={
              isSelectedAdded
                ? `Already in ${getPhaseShortLabel(activePhase)}`
                : isAnyAdded
                ? `Add variant (${getVariantLabel(addedVariant!.title, group.baseName)} already added)`
                : `Add to ${getPhaseShortLabel(activePhase)}`
            }
          >
            <Box
              component="button"
              onClick={() => !isSelectedAdded && addItem(selectedItem)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.75,
                border: 'none',
                bgcolor: 'transparent',
                color: isSelectedAdded ? theme.palette.text.complete : 'text.secondary',
                cursor: isSelectedAdded ? 'default' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                '&:hover': !isSelectedAdded
                  ? { bgcolor: alpha(theme.palette.text.complete, 0.08), color: theme.palette.text.complete }
                  : {},
              }}
            >
              {isSelectedAdded ? (
                <CheckCircleIcon sx={{ fontSize: 15 }} />
              ) : (
                <AddCircleOutlineIcon sx={{ fontSize: 15 }} />
              )}
              {isSelectedAdded ? 'Added' : `+ Add to ${getPhaseShortLabel(activePhase)}`}
            </Box>
          </Tooltip>

          {!isSetupPhase && (
            <Tooltip title="Add to multiple months">
              <Box
                component="button"
                onClick={e => handleOpenMultiMonth(e, selectedItem)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1,
                  py: 0.75,
                  border: 'none',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.08),
                    color: theme.palette.text.ai_accent_secondary,
                  },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Remove row */}
        {allVariantPhases.size > 0 && (
          <Box
            component="button"
            onClick={() => {
              group.items.forEach(item => {
                if (getItemPhases(item.products_uuid).size > 0) removeFromAll(item.products_uuid)
              })
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.5,
              border: 'none',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'transparent',
              color: 'text.disabled',
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.06), color: theme.palette.text.error },
            }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 13 }} />
            Remove from all ({allVariantPhases.size})
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ─── Single Item Card (unchanged from before, but extracted) ───────────────

const SingleItemCard: React.FC<{
  item: CatalogItem
  activePhaseItemIds: Set<string>
  getItemPhases: (productId: string) => Set<Phase>
  removeFromAll: (productId: string) => void
  addItem: (item: CatalogItem) => void
  handleOpenMultiMonth: (event: React.MouseEvent<HTMLElement>, item: CatalogItem) => void
  effectiveRate: number
  slColor: string
  SlIcon: any
  groupLabel: string
  isSetupPhase: boolean
  activePhase: Phase
  recommendedSkus: Map<string, string>
}> = ({
  item,
  activePhaseItemIds,
  getItemPhases,
  removeFromAll,
  addItem,
  handleOpenMultiMonth,
  effectiveRate,
  slColor,
  SlIcon,
  groupLabel,
  isSetupPhase,
  activePhase,
  recommendedSkus,
}) => {
  const theme = useTheme()
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'

  const isAdded = activePhaseItemIds.has(item.products_uuid)
  const cat = classifyItem(item)
  const itemPhases = getItemPhases(item.products_uuid)
  const dollarValue = (item.points || 0) * effectiveRate
  const aiReason = item.sku ? recommendedSkus.get(item.sku) : undefined
  const isRecommended = Boolean(aiReason)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isAdded
          ? alpha(theme.palette.text.complete, 0.4)
          : isRecommended
          ? alpha(aiAccent, 0.4)
          : 'divider',
        bgcolor: isAdded
          ? alpha(theme.palette.text.complete, 0.04)
          : isRecommended
          ? alpha(aiAccent, 0.04)
          : 'background.paper',
        transition: 'all 0.15s',
        overflow: 'hidden',
        '&:hover': {
          borderColor: isAdded
            ? alpha(theme.palette.text.complete, 0.5)
            : isRecommended
            ? alpha(aiAccent, 0.6)
            : alpha(theme.palette.text.primary, 0.15),
          transform: 'translateY(-1px)',
          boxShadow: isRecommended ? `0 2px 12px ${alpha(aiAccent, 0.2)}` : '0 2px 8px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Card header */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Chip
            label={cat === 'strategic' ? 'Strategic' : 'Executable'}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 600,
              bgcolor:
                cat === 'strategic' ? alpha(theme.palette.text.link, 0.12) : alpha(theme.palette.text.highlight, 0.12),
              color: cat === 'strategic' ? theme.palette.text.link : theme.palette.text.highlight,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {SlIcon && (
            <Chip
              icon={<SlIcon sx={{ fontSize: 12, color: slColor }} />}
              label={groupLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                bgcolor: `${slColor}18`,
                color: slColor,
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.25 },
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {isRecommended && (
              <Tooltip title={aiReason || 'AI Recommended'} arrow>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: aiAccent }} />
              </Tooltip>
            )}
            {item.ai_enhanced && (
              <Tooltip title="AI Enhanced Service">
                <SmartToyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            mb: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </Typography>

        {item.sku && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {item.sku}
          </Typography>
        )}
      </Box>

      {/* Points + price */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: slColor }}>
          {item.points || 0} pts
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ${dollarValue.toLocaleString()}
        </Typography>
      </Box>

      {/* Subcategory / delivery role */}
      {(item.subcategory || item.delivery_role) && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Chip
            label={item.subcategory || item.delivery_role}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              bgcolor: `${slColor}15`,
              color: slColor,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <Tooltip
            title={
              isAdded ? `Already in ${getPhaseShortLabel(activePhase)}` : `Add to ${getPhaseShortLabel(activePhase)}`
            }
          >
            <Box
              component="button"
              onClick={() => !isAdded && addItem(item)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.75,
                border: 'none',
                bgcolor: 'transparent',
                color: isAdded ? theme.palette.text.complete : 'text.secondary',
                cursor: isAdded ? 'default' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                '&:hover': !isAdded
                  ? { bgcolor: alpha(theme.palette.text.complete, 0.08), color: theme.palette.text.complete }
                  : {},
              }}
            >
              {isAdded ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : <AddCircleOutlineIcon sx={{ fontSize: 15 }} />}+
              Add to {getPhaseShortLabel(activePhase)}
            </Box>
          </Tooltip>

          {!isSetupPhase && (
            <Tooltip title="Add to multiple months">
              <Box
                component="button"
                onClick={e => handleOpenMultiMonth(e, item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1,
                  py: 0.75,
                  border: 'none',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.08),
                    color: theme.palette.text.ai_accent_secondary,
                  },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {itemPhases.size > 0 && (
          <Box
            component="button"
            onClick={() => removeFromAll(item.products_uuid)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.5,
              border: 'none',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'transparent',
              color: 'text.disabled',
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.06), color: theme.palette.text.error },
            }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 13 }} />
            Remove from all ({itemPhases.size})
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ─── Main Catalog Component ────────────────────────────────────────────────

const RoadmapCatalog: React.FC = () => {
  const {
    state,
    addItem,
    removeItem,
    effectiveRate,
    phases,
    fetchRecommendations,
    fetchRoadmap,
    reviseRoadmap,
    clearGeneratedRoadmap,
    acceptRoadmap,
    acceptRoadmapPhase,
    dispatch,
  } = useRoadmap()
  const theme = useTheme()
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'
  const aiBg = theme.palette.background.ai_accent_bg ?? alpha(aiAccent, 0.03)
  const aiBorder = theme.palette.background.ai_accent_border ?? alpha(aiAccent, 0.3)
  const [search, setSearch] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [sortMode, setSortMode] = useState<SortMode>('relevance')
  const [pointsRange, setPointsRange] = useState<number[]>([0, 100])
  const [activeServiceChip, setActiveServiceChip] = useState<string>('all')
  const [aiInput, setAiInput] = useState('')

  // Multi-month popover state
  const [multiMonthAnchor, setMultiMonthAnchor] = useState<HTMLElement | null>(null)
  const [multiMonthItem, setMultiMonthItem] = useState<CatalogItem | null>(null)
  const [selectedPhases, setSelectedPhases] = useState<Set<Phase>>(new Set())

  // Available departments from catalog
  const availableDepartments = useMemo(() => {
    const depts = new Map<string, string>()
    for (const item of state.catalog) {
      const code = normalizeServiceLine(item.service_line)
      if (!depts.has(code)) {
        depts.set(code, SERVICE_LINE_LABELS[code] || code.toUpperCase())
      }
    }
    return Array.from(depts.entries())
      .map(([code, label]) => ({ code, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [state.catalog])

  // Points min/max from catalog
  const catalogPointsRange = useMemo(() => {
    if (state.catalog.length === 0) return [0, 100]
    const pts = state.catalog.map(i => i.points || 0)
    return [Math.min(...pts), Math.max(...pts)]
  }, [state.catalog])

  useEffect(() => {
    setPointsRange(catalogPointsRange)
  }, [catalogPointsRange])

  // Filter catalog
  const filteredItems = useMemo(() => {
    let items = state.catalog
    const sl = (i: CatalogItem) => normalizeServiceLine(i.service_line)

    if (state.enabledServiceLines.size > 0) {
      items = items.filter(i => state.enabledServiceLines.has(sl(i)))
    }
    if (departmentFilter !== 'all') {
      items = items.filter(i => sl(i) === departmentFilter)
    }
    if (activeServiceChip !== 'all') {
      items = items.filter(i => sl(i) === activeServiceChip)
    }
    items = items.filter(i => {
      const pts = i.points || 0
      return pts >= pointsRange[0] && pts <= pointsRange[1]
    })
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(
        i =>
          i.title?.toLowerCase().includes(q) ||
          i.sku?.toLowerCase().includes(q) ||
          i.category_name?.toLowerCase().includes(q) ||
          i.subcategory?.toLowerCase().includes(q) ||
          i.service_line?.toLowerCase().includes(q) ||
          i.description?.toLowerCase().includes(q) ||
          i.delivery_role?.toLowerCase().includes(q)
      )
    }
    switch (sortMode) {
      case 'points_asc':
        items = [...items].sort((a, b) => (a.points || 0) - (b.points || 0))
        break
      case 'points_desc':
        items = [...items].sort((a, b) => (b.points || 0) - (a.points || 0))
        break
      case 'alpha':
        items = [...items].sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        break
      default:
        break
    }
    if (sortMode === 'relevance' && state.recommendedSkus.size > 0) {
      items = [...items].sort((a, b) => {
        const aRec = a.sku ? state.recommendedSkus.has(a.sku) : false
        const bRec = b.sku ? state.recommendedSkus.has(b.sku) : false
        if (aRec && !bRec) return -1
        if (!aRec && bRec) return 1
        return 0
      })
    }
    return items
  }, [
    state.catalog,
    state.enabledServiceLines,
    departmentFilter,
    activeServiceChip,
    pointsRange,
    search,
    sortMode,
    state.recommendedSkus,
  ])

  const activePhaseItemIds = useMemo(() => {
    return new Set(state.lineItems.filter(li => li.phase === state.activePhase).map(li => li.products_uuid))
  }, [state.lineItems, state.activePhase])

  // Group by service line, then group variants within each service line
  const grouped = useMemo(() => {
    const serviceLineGroups: Record<string, CatalogItem[]> = {}
    for (const item of filteredItems) {
      const sl = normalizeServiceLine(item.service_line)
      if (!serviceLineGroups[sl]) serviceLineGroups[sl] = []
      serviceLineGroups[sl].push(item)
    }
    return Object.entries(serviceLineGroups)
      .sort(([a], [b]) => (SERVICE_LINE_LABELS[a] || a).localeCompare(SERVICE_LINE_LABELS[b] || b))
      .map(([code, items]) => {
        const allGroups = groupItemsByVariant(items)
        const selectedGroups = allGroups.filter(vg => vg.items.some(item => activePhaseItemIds.has(item.products_uuid)))
        const availableGroups = allGroups.filter(
          vg => !vg.items.some(item => activePhaseItemIds.has(item.products_uuid))
        )
        return {
          code,
          label: SERVICE_LINE_LABELS[code] || code.toUpperCase(),
          variantGroups: allGroups,
          selectedGroups,
          availableGroups,
        }
      })
  }, [filteredItems, activePhaseItemIds])

  // Distinct card count (variant groups count as 1)
  const cardCount = useMemo(() => grouped.reduce((sum, g) => sum + g.variantGroups.length, 0), [grouped])

  const getItemPhases = useCallback(
    (productId: string) => {
      return new Set(state.lineItems.filter(li => li.products_uuid === productId).map(li => li.phase))
    },
    [state.lineItems]
  )

  const removeFromAll = useCallback(
    (productId: string) => {
      state.lineItems.filter(li => li.products_uuid === productId).forEach(li => removeItem(li.lineItemId))
    },
    [state.lineItems, removeItem]
  )

  const handleOpenMultiMonth = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: CatalogItem) => {
      event.stopPropagation()
      setMultiMonthItem(item)
      const existingPhases = getItemPhases(item.products_uuid)
      const monthPhases = phases.filter(p => p !== 'setup' && !existingPhases.has(p))
      setSelectedPhases(new Set(monthPhases.length > 0 ? [monthPhases[0]] : []))
      setMultiMonthAnchor(event.currentTarget)
    },
    [phases, getItemPhases]
  )

  const handleConfirmMultiMonth = () => {
    if (multiMonthItem) {
      selectedPhases.forEach(phase => {
        addItem(multiMonthItem, phase)
      })
    }
    setMultiMonthAnchor(null)
    setMultiMonthItem(null)
    setSelectedPhases(new Set())
  }

  const togglePhase = (phase: Phase) => {
    const next = new Set(selectedPhases)
    if (next.has(phase)) next.delete(phase)
    else next.add(phase)
    setSelectedPhases(next)
  }

  const isBuildRoadmapRequest = (text: string) => /build.*(my|a|the)?\s*roadmap/i.test(text)

  const handleAiSubmit = () => {
    if (!aiInput.trim()) return
    dispatch({ type: 'SET_AI_INSTRUCTIONS', instructions: aiInput.trim() })
    if (isBuildRoadmapRequest(aiInput.trim())) {
      fetchRoadmap()
    } else {
      fetchRecommendations()
    }
    setAiInput('')
  }

  const handleQuickAction = (prompt: string) => {
    dispatch({ type: 'SET_AI_INSTRUCTIONS', instructions: prompt })
    if (isBuildRoadmapRequest(prompt)) {
      fetchRoadmap()
    } else {
      fetchRecommendations()
    }
  }

  const isSetupPhase = state.activePhase === 'setup'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* ─── AI Strategy Assistant — Compact Toolbar ─────────────────── */}
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          px: 1,
          py: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(aiAccent, 0.2),
          bgcolor: alpha(aiAccent, 0.03),
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 16, color: aiAccent, flexShrink: 0 }} />

        {/* Quick action chips — always visible */}
        {AI_QUICK_ACTIONS.map(action => (
          <Chip
            key={action.label}
            label={action.label}
            size="small"
            onClick={() => handleQuickAction(action.prompt)}
            disabled={state.isRecommending}
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 500,
              bgcolor: 'transparent',
              border: '1px solid',
              borderColor: alpha(aiAccent, 0.15),
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': {
                borderColor: aiAccent,
                color: aiAccent,
                bgcolor: alpha(aiAccent, 0.06),
              },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        ))}

        {/* Inline chat input */}
        <TextField
          placeholder="Ask the AI..."
          size="small"
          value={aiInput}
          onChange={e => setAiInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleAiSubmit()
            }
          }}
          disabled={state.isRecommending}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {state.isRecommending ? (
                  <CircularProgress size={14} sx={{ color: aiAccent }} />
                ) : (
                  <SendIcon
                    onClick={handleAiSubmit}
                    sx={{
                      fontSize: 16,
                      color: aiInput.trim() ? aiAccent : 'text.disabled',
                      cursor: aiInput.trim() ? 'pointer' : 'default',
                    }}
                  />
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 160,
            '& .MuiOutlinedInput-root': {
              height: 30,
              fontSize: 12,
              borderRadius: 1.5,
              bgcolor: 'background.paper',
            },
          }}
        />
      </Box>

      {/* AI recommendation summary — shown inline below toolbar when present */}
      {state.recommendationSummary && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha(aiAccent, 0.04),
            border: '1px solid',
            borderColor: alpha(aiAccent, 0.12),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: aiAccent,
              fontStyle: 'italic',
              lineHeight: 1.4,
            }}
          >
            {state.recommendationSummary}
          </Typography>
        </Box>
      )}

      {/* ─── AI-Generated Roadmap Response ─────────────────────────── */}
      {(state.generatedRoadmap || state.isGeneratingRoadmap) && (
        <Box sx={{ mb: 2, maxHeight: '50vh', overflowY: 'auto' }}>
          {state.isGeneratingRoadmap ? (
            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: aiBorder,
                bgcolor: aiBg,
                p: 3,
                textAlign: 'center',
              }}
            >
              <CircularProgress size={24} sx={{ color: aiAccent, mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Building your AI roadmap...
              </Typography>
            </Box>
          ) : state.generatedRoadmap ? (
            <AIRoadmapResponse
              roadmap={state.generatedRoadmap}
              onAcceptAll={acceptRoadmap}
              onAcceptPhase={acceptRoadmapPhase}
              onRevise={reviseRoadmap}
              onDismiss={clearGeneratedRoadmap}
              onBackToSetup={() => dispatch({ type: 'SET_VIEW', view: 'setup' })}
              isAccepted={state.roadmapAccepted}
              isRevising={state.isGeneratingRoadmap}
            />
          ) : null}
        </Box>
      )}

      {/* ─── Catalog Header (adding to phase indicator) ─────────────── */}
      <Box
        sx={{
          mb: 1.5,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: alpha(aiAccent, 0.06),
          border: '1px solid',
          borderColor: alpha(aiAccent, 0.15),
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          Adding items to <b>{getPhaseShortLabel(state.activePhase)}</b>
          {isSetupPhase ? ' — diagnostics, audits, and onboarding' : ''}
        </Typography>
      </Box>

      {/* Search bar */}
      <TextField
        fullWidth
        placeholder="Search items by name, SKU, or code..."
        size="small"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
      />

      {/* Filter controls row */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <TextField
          select
          size="small"
          value={departmentFilter}
          onChange={e => setDepartmentFilter(e.target.value)}
          sx={{ minWidth: 180, '& .MuiInputBase-root': { height: 34, fontSize: 13 } }}
        >
          <MenuItem value="all">All Departments</MenuItem>
          {availableDepartments.map(d => (
            <MenuItem key={d.code} value={d.code}>
              {d.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          value={sortMode}
          onChange={e => setSortMode(e.target.value as SortMode)}
          sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 34, fontSize: 13 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SortIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="relevance">Relevance</MenuItem>
          <MenuItem value="points_desc">Points: High → Low</MenuItem>
          <MenuItem value="points_asc">Points: Low → High</MenuItem>
          <MenuItem value="alpha">Alphabetical</MenuItem>
        </TextField>
      </Box>

      {/* Points range slider */}
      <Box sx={{ px: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Points
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {pointsRange[0]} pts – {pointsRange[1]} pts
          </Typography>
        </Box>
        <Slider
          value={pointsRange}
          onChange={(_, val) => setPointsRange(val as number[])}
          min={catalogPointsRange[0]}
          max={catalogPointsRange[1]}
          size="small"
          valueLabelDisplay="auto"
          sx={{ color: 'primary.main', py: 0.5 }}
        />
      </Box>

      {/* Service line chips + Get AI Advice */}
      <Stack sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setActiveServiceChip('all')}
            sx={{
              fontWeight: 600,
              fontSize: 11,
              bgcolor: activeServiceChip === 'all' ? 'primary.main' : 'transparent',
              color: activeServiceChip === 'all' ? '#fff' : 'text.secondary',
              border: '1px solid',
              borderColor: activeServiceChip === 'all' ? 'primary.main' : 'divider',
              '&:hover': { bgcolor: activeServiceChip === 'all' ? 'primary.dark' : 'action.hover' },
            }}
          />
          {(state.enabledServiceLines.size > 0
            ? availableDepartments.filter(d => state.enabledServiceLines.has(d.code))
            : availableDepartments
          ).map(d => {
            const Icon = SERVICE_LINE_ICONS[d.code]
            const color = SERVICE_LINE_COLORS[d.code] || '#888'
            const isActive = activeServiceChip === d.code
            return (
              <Chip
                key={d.code}
                label={d.code}
                size="small"
                icon={Icon ? <Icon sx={{ fontSize: 14, color: isActive ? '#fff' : color }} /> : undefined}
                onClick={() => setActiveServiceChip(isActive ? 'all' : d.code)}
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  bgcolor: isActive ? color : 'transparent',
                  color: isActive ? '#fff' : 'text.secondary',
                  border: '1px solid',
                  borderColor: isActive ? color : 'divider',
                  '&:hover': { bgcolor: isActive ? color : 'action.hover' },
                  '& .MuiChip-icon': { ml: 0.5 },
                }}
              />
            )
          })}
        </Box>
        {/* Get AI Advice button — pushed to far right */}
        <Button
          variant={state.recommendedSkus.size > 0 ? 'outlined' : 'contained'}
          size="small"
          disabled={state.isRecommending}
          onClick={fetchRecommendations}
          sx={{
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 600,
            px: 2,
            ml: 'auto',
            flexShrink: 0,
            borderColor: state.recommendedSkus.size > 0 ? alpha(aiAccent, 0.4) : undefined,
            color: state.recommendedSkus.size > 0 ? aiAccent : undefined,
            '&:hover': state.recommendedSkus.size > 0 ? { borderColor: aiAccent, bgcolor: alpha(aiAccent, 0.08) } : {},
          }}
        >
          {state.isRecommending ? (
            <CircularProgress size={14} sx={{ mr: 0.75 }} />
          ) : (
            <AutoAwesomeIcon sx={{ fontSize: 15, mr: 0.5 }} />
          )}
          {state.isRecommending
            ? 'Analyzing...'
            : state.recommendedSkus.size > 0
            ? `AI Recommended (${state.recommendedSkus.size})`
            : 'Get AI Advice'}
        </Button>
        {/* Results count */}
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
          Showing <b>{cardCount}</b> services ({filteredItems.length} items) of <b>{state.catalog.length}</b> total
        </Typography>

        {/* Catalog grid */}
        <CatalogGrid
          state={state}
          grouped={grouped}
          activePhaseItemIds={activePhaseItemIds}
          getItemPhases={getItemPhases}
          removeFromAll={removeFromAll}
          addItem={addItem}
          handleOpenMultiMonth={handleOpenMultiMonth}
          effectiveRate={effectiveRate}
          isSetupPhase={isSetupPhase}
        />
      </Stack>
      {/* Multi-month popover */}
      <Popover
        open={Boolean(multiMonthAnchor)}
        anchorEl={multiMonthAnchor}
        onClose={() => setMultiMonthAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 220, maxHeight: 'auto' } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Add to months
        </Typography>
        {multiMonthItem && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}>
            {multiMonthItem.title} ({multiMonthItem.points || 0} pts)
          </Typography>
        )}
        {(() => {
          const availablePhases = phases.filter(
            p => p !== 'setup' && !(multiMonthItem && getItemPhases(multiMonthItem.products_uuid).has(p))
          )
          const allSelected = availablePhases.length > 0 && availablePhases.every(p => selectedPhases.has(p))
          const someSelected = availablePhases.some(p => selectedPhases.has(p))
          return availablePhases.length > 0 ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={() => {
                    if (allSelected) {
                      setSelectedPhases(new Set())
                    } else {
                      setSelectedPhases(new Set(availablePhases))
                    }
                  }}
                  sx={{ py: 0.25 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>
                  Select all months
                </Typography>
              }
              sx={{ mb: 0.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}
            />
          ) : null
        })()}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 220, overflowY: 'auto' }}>
          {phases
            .filter(p => p !== 'setup')
            .map(phase => {
              const alreadyAdded = multiMonthItem ? getItemPhases(multiMonthItem.products_uuid).has(phase) : false
              return (
                <FormControlLabel
                  key={phase}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedPhases.has(phase) || alreadyAdded}
                      disabled={alreadyAdded}
                      onChange={() => togglePhase(phase)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 12, color: alreadyAdded ? 'text.disabled' : 'text.primary' }}
                    >
                      {getPhaseShortLabel(phase)}
                      {alreadyAdded && ' (added)'}
                    </Typography>
                  }
                />
              )
            })}
        </Box>
        <Button
          variant="contained"
          size="small"
          fullWidth
          disabled={selectedPhases.size === 0}
          onClick={handleConfirmMultiMonth}
          sx={{ mt: 1.5, textTransform: 'none', fontSize: 12 }}
        >
          Add to {selectedPhases.size} month{selectedPhases.size !== 1 ? 's' : ''}
        </Button>
      </Popover>
    </Box>
  )
}

export default RoadmapCatalog
